/**
 * Centralized API Client for Beafon PIM Frontend
 * Handles automatic JWT injection, Content-Type headers, and 401 unauthorization handling.
 */

export const getAuthToken = () => {
    try {
        const token = localStorage.getItem('token');
        if (token && token !== 'null' && token !== 'undefined') {
            return token;
        }
    } catch {
        return null;
    }
    return null;
};

export const apiFetch = async (url, options = {}) => {
    const token = getAuthToken();
    const headers = { ...options.headers };

    // Inject token if available and not explicitly provided
    if (token && !headers['Authorization'] && !headers['authorization']) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // Set JSON content-type if body is a plain object or non-FormData
    if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(url, {
        ...options,
        headers
    });

    if (res.status === 401) {
        console.warn(`[apiFetch] 401 Unauthorized for ${url}. Triggering logout.`);
        // Dispatch custom event so context or components can react
        window.dispatchEvent(new CustomEvent('pim:unauthorized'));
    }

    return res;
};

export const apiGet = (url, options = {}) => {
    return apiFetch(url, { ...options, method: 'GET' });
};

export const apiPost = (url, data, options = {}) => {
    const isFormData = data instanceof FormData;
    return apiFetch(url, {
        ...options,
        method: 'POST',
        body: isFormData ? data : JSON.stringify(data)
    });
};

export const apiPut = (url, data, options = {}) => {
    const isFormData = data instanceof FormData;
    return apiFetch(url, {
        ...options,
        method: 'PUT',
        body: isFormData ? data : JSON.stringify(data)
    });
};

export const apiPatch = (url, data, options = {}) => {
    const isFormData = data instanceof FormData;
    return apiFetch(url, {
        ...options,
        method: 'PATCH',
        body: isFormData ? data : JSON.stringify(data)
    });
};

export const apiDelete = (url, options = {}) => {
    return apiFetch(url, { ...options, method: 'DELETE' });
};

// Global transparent interceptor for native window.fetch to ensure legacy components
// and background polling automatically send the Authorization Bearer header.
if (typeof window !== 'undefined' && window.fetch && !window.__pimFetchIntercepted) {
    window.__pimFetchIntercepted = true;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (input, init = {}) => {
        const url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
        if (typeof url === 'string' && (url.startsWith('/api/') || url.includes('/api/')) && !url.includes('/api/login')) {
            const token = getAuthToken();
            if (token) {
                init = init || {};
                let headers;
                if (init.headers instanceof Headers) {
                    headers = init.headers;
                } else if (Array.isArray(init.headers)) {
                    headers = new Headers(init.headers);
                } else {
                    headers = new Headers(init.headers || {});
                }

                if (!headers.has('Authorization') && !headers.has('authorization')) {
                    headers.set('Authorization', `Bearer ${token}`);
                }
                init.headers = headers;
            }
        }
        return nativeFetch(input, init);
    };
}

export default {
    getAuthToken,
    apiFetch,
    apiGet,
    apiPost,
    apiPut,
    apiPatch,
    apiDelete
};
